import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { ChartBindingValue, ChartRuleColumn, CustomChartGroupedTablePayload } from '@/entities/dashboards';
import type { ChartAppearance } from '../chartElements';
import { GROUP_COLUMN_CODE, groupedTableColumns, groupedTableRows, toIndexedRows } from './chartTable';
import { ChartTableCard } from './ChartTableCard';

type Props = {
  /** Диаграмма, чьей таблице принадлежат вид колонок, упорядочение и фильтры */
  chartId: string;
  title: string;
  payload: CustomChartGroupedTablePayload;
  /** Колонки выборки: по ним группа-дата показывается датой и упорядочивается по ней же */
  ruleColumns: ChartRuleColumn[];
  /** Оформление диаграммы: окрашиваемые колонки, цвета их значений и правила раскраски по величине */
  appearance?: ChartAppearance;
  /** Высота диаграммы задана пользователем — таблица прокручивает строки внутри неё */
  hasFixedHeight?: boolean;
  /** Открытие диалога строки; не задано — строки к диалогам не ведут */
  onOpenConversation?: (conversationId: number, openInNewTab: boolean) => void;
  /** Выбор строки наполняет связанную диаграмму; задан — выбор заменяет переход к диалогу */
  onSelectElement?: (value: ChartBindingValue) => void;
};

/**
 * Таблица с группировкой: строки собраны в группы по первому разрезу.
 *
 * Группа проставляется в свою колонку у каждой строки — так таблица остаётся одной, с общими
 * настройкой колонок, упорядочением, фильтрами и прокруткой, как остальные таблицы дашборда
 * ({@link ChartTableCard}). Выбор ведётся по группе строки: в таблице с группировкой разбирают
 * именно её.
 */
export const GroupedTableChartView: FC<Props> = ({ payload, ruleColumns, ...props }) => {
  const { t } = useTranslation('qualityDashboard');

  const tableColumns = useMemo(
    () => groupedTableColumns(payload.columns, ruleColumns, t('presets.chart.groupColumn')),
    [t, ruleColumns, payload.columns],
  );

  const indexedRows = useMemo(
    () =>
      payload.groups.flatMap((group, groupIndex) =>
        toIndexedRows(groupedTableRows(group), group.conversationIds, `${groupIndex}-`),
      ),
    [payload.groups],
  );

  return <ChartTableCard {...props} columns={tableColumns} rows={indexedRows} selectionColumn={GROUP_COLUMN_CODE} />;
};
